<script setup lang="ts">
import { computed, nextTick, reactive, ref } from 'vue'
import { NButton, NInput, useMessage } from 'naive-ui'
import ReminderRow from './ReminderRow.vue'
import ReminderSegmented from './ReminderSegmented.vue'
import { useRemindersStore, type ReminderInput } from '../stores/reminders'
import { reminderDayGroups } from '../composables/reminderDayGroups'

// The one-time-reminder half of the Reminders card (#1092): the add/edit
// composer, the Pending / Done / All filter, and the reminders grouped by
// the day they fire (Today / Tomorrow / a date).

const store = useRemindersStore()
const message = useMessage()

type ReminderStatus = 'pending' | 'done' | 'all'

const reminderMessageRef = ref<InstanceType<typeof NInput> | null>(null)

const reminderForm = reactive<ReminderInput>({ message: '', fire_at: '' })
const editingReminderId = ref<number | null>(null)
const savingReminder = ref(false)

const reminderStatus = ref<ReminderStatus>('pending')

const REMINDER_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Done', value: 'done' },
  { label: 'All', value: 'all' },
]

const dayGroups = computed(() => reminderDayGroups(store.reminders))

async function onReminderStatusChange(status: string) {
  reminderStatus.value = status as ReminderStatus
  try {
    await store.fetchReminders(reminderStatus.value)
  } catch {
    // loadError handled in the parent's template
  }
}

// Convert a stored UTC ISO string to a datetime-local value in browser local time
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

async function focusForm(): Promise<void> {
  await nextTick()
  reminderMessageRef.value?.focus()
}

async function startEditReminder(id: number) {
  const r = store.reminders.find((reminder) => reminder.id === id)
  if (!r) return
  Object.assign(reminderForm, { message: r.message, fire_at: isoToDatetimeLocal(r.fire_at) })
  editingReminderId.value = id
  await focusForm()
}

function cancelEditReminder() {
  editingReminderId.value = null
  Object.assign(reminderForm, { message: '', fire_at: '' })
}

async function saveReminder() {
  if (!reminderForm.message.trim()) {
    message.error('Message is required')
    return
  }
  if (!reminderForm.fire_at) {
    message.error('Date/time is required')
    return
  }
  savingReminder.value = true
  try {
    if (editingReminderId.value !== null) {
      await store.updateReminder(editingReminderId.value, { ...reminderForm })
      message.success('Reminder updated')
    } else {
      await store.createReminder({ ...reminderForm })
      message.success('Reminder created')
    }
    cancelEditReminder()
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save reminder')
  } finally {
    savingReminder.value = false
  }
}

async function toggleDone(id: number, done: boolean) {
  try {
    await store.toggleDone(id, done)
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to update reminder')
  }
}

async function deleteReminder(id: number) {
  try {
    await store.deleteReminder(id)
    message.success('Reminder deleted')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to delete reminder')
  }
}

defineExpose({ focusForm })
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Add / edit one-time reminder composer -->
    <div
      role="group"
      class="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-bg p-3"
      :aria-label="editingReminderId !== null ? 'Edit reminder' : 'Add one-time reminder'"
    >
      <p class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
        {{ editingReminderId !== null ? 'Edit reminder' : 'Add one-time reminder' }}
      </p>
      <NInput
        ref="reminderMessageRef"
        v-model:value="reminderForm.message"
        placeholder="e.g. Pick up dry cleaning"
        :input-props="{ 'aria-label': 'Reminder message' }"
        @keydown.enter.prevent="saveReminder"
      />
      <div class="flex flex-wrap items-center gap-2">
        <input
          v-model="reminderForm.fire_at"
          type="datetime-local"
          aria-label="Date and time (local)"
          class="min-h-10 min-w-0 flex-1 basis-52 rounded-[10px] border border-border-control bg-surface-raised px-3 text-sm text-ink [color-scheme:dark]"
        />
        <NButton type="primary" :loading="savingReminder" @click="saveReminder">
          {{ editingReminderId !== null ? 'Save changes' : 'Add reminder' }}
        </NButton>
        <NButton v-if="editingReminderId !== null" quaternary @click="cancelEditReminder">Cancel</NButton>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-2 pt-2">
      <span class="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">Show</span>
      <ReminderSegmented
        :model-value="reminderStatus"
        :options="REMINDER_STATUS_OPTIONS"
        label="Reminder status"
        @update:model-value="onReminderStatusChange"
      />
    </div>

    <p v-if="store.reminders.length === 0" class="m-0 py-6 text-center text-ink-muted">
      No {{ reminderStatus === 'all' ? '' : reminderStatus + ' ' }}reminders
    </p>
    <section v-for="group in dayGroups" :key="group.key" :aria-label="group.label" data-testid="reminder-day-group">
      <h3 class="m-0 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">
        {{ group.label }}
      </h3>
      <ReminderRow
        v-for="r in group.reminders"
        :key="r.id"
        :reminder="r"
        @toggle="toggleDone"
        @edit="startEditReminder"
        @delete="deleteReminder"
      />
    </section>
  </div>
</template>
